import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, GraduationCap, Award, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultyPage = () => {
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/faculty`);
        setFaculty(response.data);
      } catch (error) {
        console.error('Error fetching faculty:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaculty();
  }, []);

  const defaultFaculty = [
    { id: '1', name: 'Dr. Rajesh Kumar', subject_specialization: ['Physics'], experience_years: 20, qualification: 'Ph.D. IIT Delhi', rating: 4.9, bio: 'Expert in Mechanics and Electromagnetism with 20+ years of teaching experience.' },
    { id: '2', name: 'Prof. Priya Sharma', subject_specialization: ['Chemistry'], experience_years: 18, qualification: 'M.Sc. IIT Bombay', rating: 4.8, bio: 'Specializes in Organic Chemistry with innovative teaching methods.' },
    { id: '3', name: 'Dr. Amit Verma', subject_specialization: ['Mathematics'], experience_years: 15, qualification: 'Ph.D. IISc Bangalore', rating: 4.9, bio: 'Known for making complex calculus concepts simple and intuitive.' },
    { id: '4', name: 'Dr. Neha Gupta', subject_specialization: ['Biology'], experience_years: 16, qualification: 'MBBS, MD AIIMS', rating: 4.7, bio: 'AIIMS topper with extensive experience in NEET preparation.' },
    { id: '5', name: 'Prof. Sandeep Patel', subject_specialization: ['Physics', 'Mathematics'], experience_years: 22, qualification: 'M.Tech IIT Kanpur', rating: 4.8, bio: 'Dual expertise in Physics and Mathematics for integrated learning.' },
    { id: '6', name: 'Dr. Kavita Singh', subject_specialization: ['Chemistry'], experience_years: 14, qualification: 'Ph.D. IIT Madras', rating: 4.6, bio: 'Physical Chemistry expert with focus on problem-solving techniques.' },
  ];

  const displayFaculty = faculty.length > 0 ? faculty : defaultFaculty;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4">Our Team</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Meet Our <span className="text-primary">Expert Faculty</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Learn from the best minds in the industry. Our faculty comprises IITians, NITians, AIIMS doctors,
              and experienced educators who are passionate about student success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '100+', label: 'Expert Faculty' },
              { value: '15+', label: 'Avg. Experience' },
              { value: '4.8', label: 'Average Rating' },
              { value: '50K+', label: 'Students Taught' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="font-heading text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-white/70">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Faculty Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="w-24 h-24 bg-slate-200 rounded-full mx-auto mb-4"></div>
                    <div className="h-6 bg-slate-200 rounded mb-2"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayFaculty.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full card-hover overflow-hidden group" data-testid={`faculty-${member.id}`}>
                    <CardContent className="p-6">
                      <div className="relative mb-6">
                        <div className="w-28 h-28 mx-auto rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                          {member.photo_url ? (
                            <img src={member.photo_url.startsWith('http') ? member.photo_url : `${API_URL}${member.photo_url.startsWith('/') ? '' : '/'}${member.photo_url}`} alt={member.name} className="w-full h-full object-cover" />
                          ) : (
                            <GraduationCap className="w-12 h-12 text-primary/50" />
                          )}
                        </div>
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white shadow-md rounded-full px-3 py-1">
                          <Star className="w-4 h-4 fill-secondary text-secondary" />
                          <span className="text-sm font-medium">{member.rating?.toFixed(1) || '4.8'}</span>
                        </div>
                      </div>

                      <div className="text-center">
                        <h3 className="font-heading font-semibold text-xl mb-1 group-hover:text-primary transition-colors">
                          {member.name}
                        </h3>
                        <p className="text-sm text-primary font-medium mb-2">
                          {member.subject_specialization?.join(', ')}
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                          {member.qualification}
                        </p>

                        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mb-4">
                          <div className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" />
                            <span>{member.experience_years}+ years</span>
                          </div>
                        </div>

                        {member.bio && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{member.bio}</p>
                        )}

                        {member.achievements && member.achievements.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex flex-wrap gap-2 justify-center">
                              {member.achievements.slice(0, 2).map((achievement, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  <Award className="w-3 h-3 mr-1" />
                                  {achievement}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Want to Join Our <span className="text-primary">Teaching Team?</span>
          </h2>
          <p className="text-muted-foreground mb-6">
            We're always looking for passionate educators who can make a difference in students' lives.
          </p>
          <Badge className="text-base px-4 py-2">careers@calibernova.in</Badge>
        </div>
      </section>
    </Layout>
  );
};

export default FacultyPage;
